import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { Toaster as HotToaster } from 'react-hot-toast';
import { toast } from 'react-toastify';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { LuFileBadge, LuFolderOpen, LuIdCard, LuReceipt, LuTruck } from 'react-icons/lu';
import { auth, db, storage } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { CARGO_ACCESS_FIELD } from '../../utils/companyShipments';
import { QUIZ_SOURCE } from '../../utils/questionPool';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './ProfilePage.module.css';
import PoolQuiz from '../../components/PoolQuiz/PoolQuiz';
import ExpenseRequests from '../../components/ExpenseRequests';
import CargoTracking from '../StockManagement/CargoTracking/CargoTracking';
import EducationModule from './EducationModule';
import EducationPlayer from './EducationPlayer';
import Agenda from './Agenda';
import Notifications from './Notifications';
import PersonalCalendar from './calendar/PersonalCalendar';
import ProfileHero from './components/ProfileHero';
import ProfileNav from './components/ProfileNav';
import { AddressDialog, EmergencyPhoneDialog, PasswordDialog } from './components/AccountDialogs';
import { HelpNote, SectionHeader } from './components/ProfileUi';
import ComplaintsSection from './complaints/ComplaintsSection';
import CvDocuments from './cv/CvDocuments';
import IncomingComplaints from './complaints/IncomingComplaints';
import ErrorReportForm from './contact/ErrorReportForm';
import MessagesPanel from './contact/MessagesPanel';
import ExpenseRequestForm from './expense/ExpenseRequestForm';
import ExpenseTracking from './expense/ExpenseTracking';
import { useMyExpenseRequests } from './expense/expenseOptions';
import FilesSection from './files/FilesSection';
import SopViewerModal from './files/SopViewerModal';
import useAssignedDocuments from './files/useAssignedDocuments';
import useDocumentLibrary from './files/useDocumentLibrary';
import LeaveRequestForm from './leave/LeaveRequestForm';
import LeaveRequestList from './leave/LeaveRequestList';
import useLeaveRequests from './leave/useLeaveRequests';
import ProjectsSection, { isPassiveProject } from './projects/ProjectsSection';
import { DEFAULT_TAB, TAB_GROUPS, groupOfTab, isTabVisible, tabsOfGroup } from './profileTabs';
import useProfileCompletion from './useProfileCompletion';
import useProfileData from './useProfileData';

const CV_DOCUMENTS = {
  'cv-tr': { documentType: 'CV-TR' },
  'cv-en': { documentType: 'CV-EN' },
  gcp: { documentType: 'GCP' },
  iata: { documentType: 'IATA', allowMultiple: true },
};

const fadeTab = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

/**
 * "Profilim": the one page every employee uses. A profile card with the
 * contact details, then the sections: calendar (the default), files,
 * projects, leave, expenses, messages, petitions, CV documents and cargo.
 * Other screens open a section with `navigate('/profile', { state: { activeTab } })`.
 */
const ProfilePage = () => {
  const location = useLocation();
  const { t, language } = useTranslation();
  const { profileImageUrl, setProfileImageUrl, employeeData, employeeId } = useUserProfile();
  const profile = useProfileData({ setProfileImageUrl });
  const { userData, setUserData, isUserAdmin, isComplaintTarget, totalUnreadMessages } = profile;

  // Profilden kargo: yöneticiler + Stok Yönetimi › Kargo Takip › Yetkiler'den seçilenler
  const canUseCargo = isUserAdmin || employeeData?.[CARGO_ACCESS_FIELD] === true;

  const library = useDocumentLibrary(userData.role);
  const assignedDocs = useAssignedDocuments(userData.role);
  const completion = useProfileCompletion(userData, profileImageUrl, library);

  const [activeTab, setActiveTab] = useState(() => location.state?.activeTab || DEFAULT_TAB);
  const lastSubTab = useRef({});

  // Mesaj bildiriminden / üst çubuktaki zarftan gelindiyse mesajlar beklemeden yüklenir
  const [messengerLoaded, setMessengerLoaded] = useState(location.state?.activeTab === 'mesajlar' || !!location.state?.openChatWith);
  const [messengerAutoOpened, setMessengerAutoOpened] = useState(location.state?.activeTab === 'mesajlar' || !!location.state?.openChatWith);
  const [requestedChatEmail, setRequestedChatEmail] = useState(location.state?.openChatWith || null);

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);

  const leaveTab = activeTab === 'izin-talep' || activeTab === 'izin-taleplerim' ? activeTab : null;
  const leave = useLeaveRequests({ employeeId, userData, active: leaveTab });
  const expenses = useMyExpenseRequests(employeeId, activeTab === 'masraf-takip');

  const permissions = useMemo(() => ({ isAdmin: isUserAdmin, isComplaintTarget, canUseCargo }), [isUserAdmin, isComplaintTarget, canUseCargo]);
  const visibleGroups = useMemo(() => TAB_GROUPS
    .map((group) => ({ ...group, visibleTabs: tabsOfGroup(group).filter((tab) => isTabVisible(tab, permissions)) }))
    .filter((group) => group.visibleTabs.length > 0), [permissions]);

  // Bilinmeyen sekme anahtarıyla gelinirse takvim açılır
  useEffect(() => {
    if (!groupOfTab(activeTab)) setActiveTab(DEFAULT_TAB);
  }, [activeTab]);

  const activeGroup = groupOfTab(activeTab)?.key || groupOfTab(DEFAULT_TAB).key;
  if (groupOfTab(activeTab)?.tabs) lastSubTab.current[activeGroup] = activeTab;

  const selectGroup = (groupKey) => {
    const group = visibleGroups.find((item) => item.key === groupKey);
    if (!group) return;
    const remembered = lastSubTab.current[groupKey];
    setActiveTab(group.visibleTabs.includes(remembered) ? remembered : group.visibleTabs[0]);
  };

  // Profil kartındaki kısayollar; okunmamış mesajlara gidince mesajlar hemen yüklenir
  const openTab = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'mesajlar') setMessengerLoaded(true);
  }, []);

  // Sayfa zaten açıkken gelen yönlendirmeler (mesaj bildirimi, zarf simgesi)
  useEffect(() => {
    const navState = location.state;
    if (!navState?.activeTab && !navState?.openChatWith) return;
    if (navState.activeTab) setActiveTab(navState.activeTab);
    if (navState.activeTab === 'mesajlar' || navState.openChatWith) {
      setMessengerLoaded(true);
      setMessengerAutoOpened(true);
    }
    if (navState.openChatWith) setRequestedChatEmail(navState.openChatWith);
  }, [location.state]);

  // Kayıttaki indirme adresi eskimiş olabilir: yüklenemeyen adres için Storage'dan
  // güncel adres bir kez istenir. Farklı bir adres gelirse true döner.
  const triedPhotoUrls = useRef(new Set());
  const freshPhotoUrl = useRef(null);
  const refreshPhotoUrl = useCallback(async (failedUrl) => {
    const user = auth.currentUser;
    if (!user) return false;
    // Eski adres sonradan yeniden gelirse daha önce bulunan güncel adrese dönülür
    if (freshPhotoUrl.current && freshPhotoUrl.current !== failedUrl) {
      setProfileImageUrl(freshPhotoUrl.current);
      return true;
    }
    if (triedPhotoUrls.current.has(failedUrl)) return false;
    triedPhotoUrls.current.add(failedUrl);
    try {
      const freshUrl = await getDownloadURL(ref(storage, `profileImages/${user.uid}.jpg`));
      if (freshUrl && freshUrl !== failedUrl) {
        freshPhotoUrl.current = freshUrl;
        setProfileImageUrl(freshUrl);
        return true;
      }
    } catch (photoError) {
      if (photoError.code !== 'storage/object-not-found') console.error('Profile image could not be refreshed:', photoError);
    }
    return false;
  }, [setProfileImageUrl]);

  const handleImageUpload = async (file) => {
    try {
      const user = auth.currentUser;
      const imageRef = ref(storage, `profileImages/${user.uid}.jpg`);
      await uploadBytes(imageRef, file);
      const newUrl = await getDownloadURL(imageRef);
      freshPhotoUrl.current = newUrl;
      setProfileImageUrl(newUrl);

      const querySnapshot = await getDocs(query(collection(db, 'Employees'), where('eMail', '==', user.email)));
      if (!querySnapshot.empty) {
        await updateDoc(doc(db, 'Employees', querySnapshot.docs[0].id), { profileImageUrl: newUrl });
      }
      toast.success(t('profilePage.hero.photoSaved'));
      return true;
    } catch (error) {
      console.error('Error uploading image: ', error);
      toast.error(t('profilePage.hero.photoError'));
      return false;
    }
  };

  const activeProjectCount = useMemo(
    () => (userData.projects || []).filter((project) => !isPassiveProject(project)).length,
    [userData.projects]
  );

  const badges = {
    dosyalar: library.counts.newSops + library.counts.newForms + library.counts.newEducations,
    'iletisim-bildirim': totalUnreadMessages,
    mesajlar: totalUnreadMessages,
  };

  const legacyHeader = (icon, gradient, key) => (
    <SectionHeader icon={icon} gradient={gradient} title={t(`profilePage.sections.${key}.title`)} description={t(`profilePage.sections.${key}.description`)} />
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'takvim':
        return <PersonalCalendar />;
      case 'ajandam':
        return <Agenda />;
      case 'dosyalar':
        return (
          <div className="tw-space-y-4">
            <SectionHeader
              icon={LuFolderOpen}
              gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30"
              title={t('profilePage.files.title')}
              description={t('profilePage.files.description')}
            />
            <FilesSection library={library} assignedDocs={assignedDocs} />
          </div>
        );
      case 'projelerim':
        return <ProjectsSection projects={userData.projects || []} employeeId={employeeId} />;
      case 'izin-talep':
        return (
          <LeaveRequestForm
            userData={userData}
            employeeId={employeeId}
            myLeaveRequests={leave.myLeaveRequests}
            onSubmitted={leave.fetchMyLeaveRequests}
          />
        );
      case 'izin-taleplerim':
        return <LeaveRequestList requests={leave.myLeaveRequests} loading={leave.loading} onNewRequest={() => setActiveTab('izin-talep')} />;
      case 'masraf-talep':
        return <ExpenseRequestForm userData={userData} employeeData={employeeData} employeeId={employeeId} onShowTracking={() => setActiveTab('masraf-takip')} />;
      case 'masraf-takip':
        return (
          <ExpenseTracking
            requests={expenses.requests}
            setRequests={expenses.setRequests}
            loading={expenses.loading}
            employeeId={employeeId}
            onNewRequest={() => setActiveTab('masraf-talep')}
          />
        );
      case 'masraf-talepleri':
        return isUserAdmin ? (
          <div className="tw-space-y-4">
            {legacyHeader(LuReceipt, 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', 'expenseApprovals')}
            <ExpenseRequests />
          </div>
        ) : null;
      case 'mesajlar':
        return (
          <MessagesPanel
            users={profile.users}
            loaded={messengerLoaded}
            onLoad={() => setMessengerLoaded(true)}
            autoOpened={messengerAutoOpened}
            requestedChatEmail={requestedChatEmail}
            onInitialChatOpened={() => setRequestedChatEmail(null)}
            unreadCount={totalUnreadMessages}
          />
        );
      case 'bildirimler':
        return <Notifications isAdmin={userData.role === 'admin'} />;
      case 'sistem-hata-bildirimleri':
        return <ErrorReportForm userData={userData} />;
      case 'sikayetlerim':
        return isUserAdmin || isComplaintTarget ? <ComplaintsSection userData={userData} /> : null;
      case 'gelen-sikayetler':
        return isUserAdmin || isComplaintTarget ? <IncomingComplaints isAdmin={isUserAdmin} isComplaintTarget={isComplaintTarget} userData={userData} /> : null;
      case 'cv-tr':
      case 'cv-en':
      case 'gcp':
      case 'iata':
        return (
          <div className="tw-space-y-4">
            {legacyHeader(activeTab === 'gcp' || activeTab === 'iata' ? LuFileBadge : LuIdCard, 'tw-from-sky-400 tw-to-indigo-600 tw-shadow-sky-500/30', activeTab)}
            <CvDocuments
              key={activeTab}
              employeeId={employeeId}
              userData={userData}
              documentType={CV_DOCUMENTS[activeTab].documentType}
              allowMultiple={CV_DOCUMENTS[activeTab].allowMultiple}
            />
          </div>
        );
      case 'kargolarim':
        return canUseCargo ? (
          <div className="tw-space-y-4">
            {legacyHeader(LuTruck, 'tw-from-orange-400 tw-to-rose-500 tw-shadow-orange-500/30', 'cargo')}
            <CargoTracking mode="personal" />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        {/* Görüntülü arama bildirimleri (Teleconference/VideoCallContext) react-hot-toast kullanıyor */}
        <HotToaster position="top-right" toastOptions={{ className: 'tw-rounded-2xl tw-text-sm' }} />

        {profile.error && <HelpNote tone="danger">{t('profilePage.loadError')}</HelpNote>}

        <ProfileHero
          userData={userData}
          loading={profile.loading}
          profileImageUrl={profileImageUrl}
          onUploadPhoto={handleImageUpload}
          onPhotoError={refreshPhotoUrl}
          completion={completion}
          stats={{
            activeProjects: activeProjectCount,
            sopRead: library.counts.readSops,
            sopTotal: library.mainSOPList.length,
            unreadMessages: totalUnreadMessages,
          }}
          onOpenTab={openTab}
          onEditAddress={() => setShowAddressDialog(true)}
          onEditPhone={() => setShowPhoneDialog(true)}
          onChangePassword={() => setShowPasswordDialog(true)}
        />

        <ProfileNav
          groups={visibleGroups}
          activeGroup={activeGroup}
          activeTab={activeTab}
          onSelectGroup={selectGroup}
          onSelectTab={setActiveTab}
          badges={badges}
        />

        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={activeTab} {...fadeTab} className="tw-min-w-0">
            {renderTab()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dosyalar sekmesinden açılan görüntüleyiciler ve sınav (sayfa animasyonlarının dışında) */}
      <SopViewerModal
        sop={library.selectedSOP}
        isRead={Boolean(library.selectedSOP && library.readSOPs.has(library.selectedSOP.id))}
        canConfirm={library.hasScrolledToBottom}
        onConfirmReady={() => library.setHasScrolledToBottom(true)}
        onConfirm={() => {
          const sop = library.selectedSOP;
          if (!sop) return;
          if (library.readSOPs.has(sop.id)) library.toggleSOPRead(sop.id);
          // Okundu onayı, departman soru havuzundan gelen sınav geçilince verilir
          else if (library.hasScrolledToBottom) library.setSopQuizTarget(sop);
        }}
        onClose={library.closeSOP}
        quizOpen={Boolean(library.sopQuizTarget)}
      />
      <PoolQuiz
        show={Boolean(library.sopQuizTarget)}
        group={userData.role}
        source={{ type: QUIZ_SOURCE.SOP, id: library.sopQuizTarget?.id || '', name: library.sopQuizTarget?.name || '' }}
        user={{
          uid: auth.currentUser?.uid,
          employeeId,
          name: `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim(),
        }}
        onPassed={() => {
          const sopId = library.sopQuizTarget?.id;
          library.setSopQuizTarget(null);
          if (sopId) library.toggleSOPRead(sopId);
        }}
        onClose={() => library.setSopQuizTarget(null)}
      />
      {library.selectedEducationSOP && (
        <EducationModule
          show={library.showEducationModule}
          onHide={() => library.setShowEducationModule(false)}
          sopData={library.selectedEducationSOP}
          onComplete={library.handleEducationComplete}
        />
      )}
      {library.selectedIndicationEducation && (
        <EducationPlayer
          show={library.showEducationPlayer}
          onHide={library.closeIndicationEducation}
          education={library.selectedIndicationEducation}
          onComplete={library.handleIndicationEducationComplete}
        />
      )}

      <AddressDialog isOpen={showAddressDialog} userData={userData} onClose={() => setShowAddressDialog(false)} />
      <EmergencyPhoneDialog
        isOpen={showPhoneDialog}
        userData={userData}
        onClose={() => setShowPhoneDialog(false)}
        onSaved={(emergencyPhone) => setUserData((prev) => ({ ...prev, emergencyPhone }))}
      />
      <PasswordDialog isOpen={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} />
    </MotionConfig>
  );
};

export default ProfilePage;
